import { useCallback, useEffect, useState } from 'react';
import { formatRM, type SaleDetail, type SaleSummary, type TodayReport } from '@bazaar/shared';
import { api, ApiError } from '../lib/api.js';
import { useToast } from '../context/ToastContext.js';
import { Spinner } from '../components/Spinner.js';
import { EmptyState } from '../components/EmptyState.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';
import { BigButton } from '../components/BigButton.js';
import { Page, PageHeader, Section, Split } from '../components/layout.js';

const PAYMENT_LABELS: Record<string, string> = { cash: 'Cash', qr: 'QR', card: 'Card', other: 'Other' };

function formatTime(iso: string): string {
  return new Intl.DateTimeFormat('en-MY', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'Asia/Kuala_Lumpur',
  }).format(new Date(iso));
}

export function TodayPage() {
  const { showToast } = useToast();
  const [report, setReport] = useState<TodayReport | null>(null);
  const [sales, setSales] = useState<SaleSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSale, setSelectedSale] = useState<SaleDetail | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [pendingVoidId, setPendingVoidId] = useState<number | null>(null);
  const [voiding, setVoiding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [reportRes, salesRes] = await Promise.all([api.getTodayReport(), api.listSalesToday()]);
      setReport(reportRes);
      setSales(salesRes.sales);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load today’s report');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function openDetail(saleId: number) {
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const detail = await api.getSale(saleId);
      setSelectedSale(detail);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : 'Could not load sale details', 'error');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  }

  function closeDetail() {
    setDetailOpen(false);
    setSelectedSale(null);
  }

  async function confirmVoid() {
    if (pendingVoidId === null) return;
    setVoiding(true);
    try {
      await api.voidSale(pendingVoidId);
      showToast('Sale voided', 'success');
      setPendingVoidId(null);
      closeDetail();
      await load();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : 'Could not void this sale', 'error');
      setPendingVoidId(null);
    } finally {
      setVoiding(false);
    }
  }

  if (loading && !report) {
    return <Spinner label="Loading today’s report" />;
  }
  if (error) {
    return <EmptyState title="Could not load today" description={error} />;
  }
  if (!report) return null;

  return (
    <Page>
      <PageHeader title="Today" />

      <Split
        mobileAside="first"
        aside={
          <>
            <section className="card flex flex-col items-center text-center gap-1">
              <p className="font-body text-sm text-muted">Today’s sales</p>
              <p className="font-heading font-semibold text-[32px] text-ink">{formatRM(report.totals.totalCents)}</p>
              <p className="font-body text-sm text-muted">
                {report.totals.saleCount} sale{report.totals.saleCount === 1 ? '' : 's'} &middot;{' '}
                {report.totals.itemCount} item{report.totals.itemCount === 1 ? '' : 's'} sold
                {report.totals.voidedCount > 0 ? ` · ${report.totals.voidedCount} voided` : ''}
              </p>
            </section>

            <Section title="By payment method">
              <div className="chip-row">
                {report.byPaymentMethod.map((m) => (
                  <span
                    key={m.method}
                    className="rounded-full bg-aqua text-teal-dark font-body font-semibold text-sm px-3 py-1.5"
                  >
                    {PAYMENT_LABELS[m.method] ?? m.method}: {formatRM(m.totalCents)} ({m.saleCount})
                  </span>
                ))}
              </div>
            </Section>
          </>
        }
      >
        <Section title="My sales today">
          {sales.length === 0 ? (
            <EmptyState title="No sales yet" description="Sales you record today will show up here." />
          ) : (
            <ul className="list-stack">
              {sales.map((sale) => (
                <li key={sale.id}>
                  <button
                    type="button"
                    onClick={() => void openDetail(sale.id)}
                    className="card-row w-full text-left flex items-center justify-between gap-3"
                  >
                    <div>
                      <p className="font-body font-semibold text-base text-text">
                        #{sale.id} &middot; {formatTime(sale.createdAt)}
                      </p>
                      <p className="font-body text-sm text-muted">
                        {PAYMENT_LABELS[sale.paymentMethod] ?? sale.paymentMethod} &middot; {sale.itemCount} item
                        {sale.itemCount === 1 ? '' : 's'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {sale.voidedAt && (
                        <span className="rounded-full bg-orange text-ink font-heading font-bold text-sm px-2 py-1">
                          VOIDED
                        </span>
                      )}
                      <span className="font-heading font-semibold text-lg text-ink">{formatRM(sale.totalCents)}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </Split>

      {detailOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-ink/40 sm:p-6"
          role="presentation"
          onClick={closeDetail}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="sale-detail-title"
            className="w-full max-w-lg bg-surface rounded-t-2xl sm:rounded-2xl shadow-xl p-4 sm:p-6 flex flex-col gap-4 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {detailLoading || !selectedSale ? (
              <Spinner label="Loading sale" />
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <h2 id="sale-detail-title" className="font-heading font-semibold text-xl text-ink">
                    Sale #{selectedSale.id}
                  </h2>
                  <button
                    type="button"
                    onClick={closeDetail}
                    aria-label="Close sale details"
                    className="min-h-12 min-w-12 rounded-full text-muted"
                  >
                    &times;
                  </button>
                </div>

                {selectedSale.voidedAt && (
                  <span className="self-start rounded-full bg-orange text-ink font-heading font-bold text-sm px-2 py-1">
                    VOIDED
                  </span>
                )}

                <ul className="flex flex-col gap-2">
                  {selectedSale.items.map((item) => (
                    <li key={item.id} className="flex justify-between font-body text-base text-text">
                      <span>
                        {item.qty} &times; {item.productName} ({item.code})
                      </span>
                      <span>{formatRM(item.lineTotalCents)}</span>
                    </li>
                  ))}
                </ul>

                <dl className="flex flex-col gap-1 pt-2 border-t border-border">
                  <div className="flex justify-between font-body text-base text-text">
                    <dt>Subtotal</dt>
                    <dd>{formatRM(selectedSale.subtotalCents)}</dd>
                  </div>
                  {selectedSale.autoDiscountCents > 0 && (
                    <div className="flex justify-between font-body text-base text-text">
                      <dt>Automatic discount{selectedSale.autoDiscountLabel ? ` (${selectedSale.autoDiscountLabel})` : ''}</dt>
                      <dd>&minus;{formatRM(selectedSale.autoDiscountCents)}</dd>
                    </div>
                  )}
                  {selectedSale.discountCents > 0 && (
                    <div className="flex justify-between font-body text-base text-text">
                      <dt>Discount</dt>
                      <dd>&minus;{formatRM(selectedSale.discountCents)}</dd>
                    </div>
                  )}
                  <div className="flex justify-between font-heading font-semibold text-xl text-ink">
                    <dt>Total</dt>
                    <dd>{formatRM(selectedSale.totalCents)}</dd>
                  </div>
                </dl>

                {!selectedSale.voidedAt && (
                  <BigButton variant="danger" onClick={() => setPendingVoidId(selectedSale.id)}>
                    Void sale
                  </BigButton>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={pendingVoidId !== null}
        title="Void this sale?"
        description="Stock for its items will be restored. This cannot be undone."
        confirmLabel={voiding ? 'Voiding…' : 'Yes, void sale'}
        variant="danger"
        onConfirm={() => void confirmVoid()}
        onCancel={() => setPendingVoidId(null)}
      />
    </Page>
  );
}
